/* Built In Imports */
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box, Heading } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';

/* Internal Imports */
/* Components */
import MidContainer from '@Layout/MidContainer';
import config from '@config';
import PageConfig from '@lb/Common/PageConfig';
import StructuredTextContent from '@lb/Common/StructuredTextContent';
import FaqExpandedSection from '@lb/components/Accordions/FaqExpandedSection';
import TopBannerBlog from '@lb/components/Banners/TopBannerBlog';
import RelatedContentSlider from '@lb/components/Blogs/RelatedContentSlider';
import BlogTopics from '@lb/components/Chips/BlogTopics';
import MediaEmbed from '@lb/components/Embeds/MediaEmbed';
import WrapLayout from '@lb/components/Layout/WrapLayout';
import Breadcrum from '@lb/components/Navigation/Breadcrum';
import HeadComponent from '@lb/components/Utility/HeadComponent';
import StructuredTextParser from '@lb/components/Utility/StructuredTextParser';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';
import {
    fetchFooterDetailData,
    fetchHeaderMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/lbService';

/**
 * @param {object} pageData
 * @param lang
 * @param region
 * @returns
 */

const VideoDetail = ({ pageData, region, lang }) => {
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, lang);
  }, [authContext, region, lang]);

  const authorSchema =
    pageData.authorStructuredData === 'Sadhguru'
      ? [
        {
          '@type': 'Person',
          name: 'Sadhguru Jaggi Vasudev',
          url: 'https://isha.sadhguru.org',
          image: '',
          sameAs: [
            'https://www.facebook.com/sadhguru/',
            'https://twitter.com/SadhguruJV',
            'https://www.instagram.com/sadhguru/?hl=en',
            'https://www.youtube.com/c/sadhguru',
            'https://isha.sadhguru.org',
            'https://www.youtube.com/channel/UCsVCyZFXPPL_iYD8-O2UvrQ',
            'https://www.youtube.com/user/SadhguruAnandLahar',
            'https://www.youtube.com/channel/UCJoOIj9Yu71wklP3EFzL8-A',
            'https://www.youtube.com/channel/UCNxOfQA4r5BSJMIQzRPIeQw',
            'https://www.youtube.com/user/SadhguruRussia',
          ],
          jobTitle: 'Spiritual Guru',
        },
      ]
      : [
        {
          '@type': 'Organization',
          name: 'Isha Foundation',
          url: 'https://www.instagram.com/isha.foundation/?hl=en',
          logo: '',
          sameAs: [
            'https://www.facebook.com/ishafoundation/',
            'https://twitter.com/ishafoundation',
            'https://www.instagram.com/isha.foundation/?hl=en',
            'https://www.youtube.com/channel/UCgaiWfiix1zaQS6Mn5SIw2g',
          ],
        },
      ];
  
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageData.title,
    },
    headline: pageData.title,
    image: pageData.heroImage?.url,
    description: pageData.summary,
    author: authorSchema,

    publisher: {
      '@type': 'Organization',
      name: 'Isha Foundation',
      logo: {
        '@type': 'ImageObject',
        url: `${config.staticPath}/d/46272/1664011373-logo-white.png`
      },
    },
    datePublished: pageData.createdAt,
    dateModified: pageData.updatedAt,
  };

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      {pageData?.breadcrumbs?.length > 0 && (
        <Breadcrum
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url=""
          region={region}
          lang={lang}
          pageConfig={pageData.pageConfig}
        />
      )}

      <Box
        as="section"
        background={`url(${config.imgPath}/d/46272/1682095879-white-texture-final.png)`}
        className="container-article"
        p="0"
      >
        <TopBannerBlog
          pageData={pageData}
          region={region}
          language={lang}
        />
        <MidContainer
          w="1330px"
          position="relative"
          // my={{ base: '40px', md: '60px' }}
          px={{ base: '15px', md: '0' }}
          zIndex="1"
        >
          <Box
            position="relative"
            _after={{
              content: "''",
              position: 'absolute',
              width: '100%',
              height: '142px',
              bottom: 0,
              left: 0,
              background:
                'linear-gradient(180deg, #D5CAAE 0%, rgba(255, 255, 255, 0) 100%)',
              transform: 'matrix(1, 0, 0, -1, 0, 0)',
              zIndex: -1
            }}
            py={{ base: '30px', md: '67px' }}
          >
            <Box fontSize="16px" mb="43px">
              {StructuredTextContent(pageData?.body, region, lang, 'blog', pageData)}
            </Box>
            <Box fontSize="16px" mb="43px">
              <StructuredTextParser
                px="0"
                str={render(pageData?.editorsNote)}
                textAlign="left"
              />
            </Box>
            <Box w="100%" maxW="768px" m="0 auto">
              <Heading
                as="h4"
                fontSize={{ base: '26px', md: '32px' }}
                lineHeight={{ base: '34px', md: '42px' }}
                mt="60px"
                mb="15px"
                color="#65000F"
                ml={{ base: '16px', md: 3, lg: 0 }}
                fontFamily="'FedraSerifAStdBook', system-ui, sans-serif"
                fontWeight="600"
              >
                Tags
              </Heading>
              <BlogTopics
                topics={pageData?.related_tags}
                region={region}
                lang={lang}
              />
            </Box>
          </Box>
        </MidContainer>
                
        {pageData?.video?.url && (
          <MediaEmbed sectionContent={pageData?.video} />
        )}
        {pageData?.faqStructuredData?.length > 0 && (
          <FaqExpandedSection
            sectionContent={pageData?.faqStructuredData}
            region={region}
            lang={lang}
          />
        )}
        {pageData?.related_content?.length > 0 && (
          <RelatedContentSlider
            region={region}
            lang={lang}
            sectionContent={pageData?.related_content}
            background="none"
          />
        )}
      </Box>
      <PageConfig isLikeVisible={false} pageData={pageData} /><script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const lbPaths = config.USE_LOCAL_URL
    ? [`/linga-bhairavi/in/en/video/how-does-bhairavi-yantra-support-benefit`, `/linga-bhairavi/in/en/video/10-000-women-culminate-an-intense-sadhana-at-linga-bhairavi-s10th-anniversary`]
    : await getLandingPaths('L2', '/video/', 'linga_bhairavi_blog');
  return {
    paths: lbPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, lang, video },
}) => {
  const topMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbtopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale(region, lang, 'lb'),
    'lbfooter'
  );
  const pageData = await fetchDetails(region, lang, `video/${video}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      topMenuData: topMenuData,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      region: region,
      lang: lang,
      menuType: 'lb',
    },
  };
};

export default VideoDetail;
